/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useMemo } from 'react';

import Imgix from 'js/components/Imgix';
import imgix from 'js/lib/imgix';

import { Button, Grid, IconButton, Typography, Typography2, breakpoints } from '@coursera/cds-core';

import type { DdpFieldsAboveFoldFragment } from 'bundles/degree-description/__generated__/contentfulTypes';
import { useDegreeDescriptionPageTheme } from 'bundles/degree-description/degreeDescriptionPageTheme';
import { useDegreeDescriptionEventingContext } from 'bundles/degree-description/providers/DegreeDescriptionEventingProvider';
import Carousel from 'bundles/design-system/components/Carousel';
import EoiButton from 'bundles/expression-of-interest/components/EoiButton';
import { ACTION_TYPES } from 'bundles/expression-of-interest/constants';
import type { EoiButtonsType } from 'bundles/expression-of-interest/types';
import { isPerformancePathwayDegree } from 'bundles/expression-of-interest/utils/util';

import TestimonialCTAImage from 'images/degree-description/testimonial.png';

import _t from 'i18n!nls/degree-description';

import Quote from './Quote';
import TestimonialVideo from './TestimonialVideo';

const styles = {
  sliderDots: css`
    .carousel-slider-dots {
      cursor: pointer;
      position: absolute;
      bottom: 24px;
      display: block;
      width: 100%;
      padding: 0;
      list-style: none;
      text-align: center;

      li {
        display: inline-block;
        height: 16px;
        width: 16px;
        margin: 0 6px;
        padding: 0;
      }

      button {
        position: relative;
        height: 16px;
        width: 16px;
        border-radius: 10px;
        border: 2px solid var(--cds-color-neutral-primary-weak);
        background-color: transparent;
      }

      .slick-active {
        button {
          background-color: var(--cds-color-neutral-primary-weak);
        }
      }
    }
  `,

  carouselWrap: css`
    .rc-Carousel .slick-list {
      /* Matches default slick next/prev transition time */
      transition: height 500ms;
    }

    .rc-Carousel.stepped .slick-slider {
      ${breakpoints.only('md')} {
        width: 1024px;
      }
    }
  `,
  carouselButtons: css`
    .slick-prev,
    .slick-next {
      position: absolute;
      padding: 0;
      cursor: pointer;

      ${breakpoints.up('md')} {
        top: calc(50% - 24px);
      }

      ${breakpoints.down('md')} {
        top: 50%;
      }
    }

    .slick-prev {
      position: absolute;
      left: 24px;
      z-index: 1;
    }

    .slick-next {
      position: absolute;
      right: 24px;
      z-index: 1;
    }

    [dir='rtl'] {
      .slick-prev {
        right: -30px;
        left: auto;
      }

      .slick-next {
        right: auto;
        left: -30px;
      }

      .slick-slide {
        float: right;
      }
    }
  `,
  testimonialCard: css`
    background: var(--cds-color-neutral-background-primary);
    margin: 48px;
    margin-bottom: 80px;
    min-width: 0;
  `,
  testimonialGrid: css`
    ${breakpoints.up('sm')} {
      min-height: 434px;
    }
    ${breakpoints.down('sm')} {
      min-height: 200px;
    }
  `,
  carouselButtonIconWrap: css`
    display: flex;
  `,
  imageContainer: css`
    img {
      /* Need to set as important to override Imgix component styles */
      max-height: unset !important;
      max-width: unset !important;

      ${breakpoints.up('md')} {
        border-radius: 8px;
        margin-left: 48px;
        width: calc(100% - 48px);
      }
      ${breakpoints.down('sm')} {
        border-top-left-radius: 4px;
        border-top-right-radius: 4px;
        width: 100%;
      }
    }
  `,
  lastSlideContent: css`
    background-color: var(--cds-color-emphasis-primary-background-strong);
    padding: 80px 48px;
    position: relative;
    height: 434px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    ${breakpoints.down('sm')} {
      padding: 64px 24px;
      height: 348px;
    }
  `,
  lastSlideImageContainer: css`
    img {
      height: 434px;
      max-height: unset !important;
      max-width: unset !important;
      object-fit: cover;
      width: 100%;
      ${breakpoints.down('sm')} {
        border-top-left-radius: 4px;
        border-top-right-radius: 4px;
        height: unset;
      }
    }
  `,
  lastSlideTestimonialGrid: css`
    ${breakpoints.up('sm')} {
      min-height: 434px;
    }
    ${breakpoints.down('sm')} {
      min-height: 434px;
    }
  `,
  textMargin: css`
    margin: 48px 0;
    ${breakpoints.down('sm')} {
      margin: 32px 0;
    }
  `,
  ctaButton: css`
    width: 214px;
    height: 48px;

    & button {
      background-color: var(--cds-color-neutral-background-primary);
      color: var(--cds-color-blue-800);

      &:hover {
        width: 214px;
        height: 48px;
        background-color: var(--cds-color-neutral-background-primary);
        color: var(--cds-color-blue-800);
      }
    }
  `,
};

const TestimonialCTA: React.FC<{
  applicationUrl?: string | null;
  eoiButtons: EoiButtonsType;
  slug: string;
  trackingProductId: string;
}> = ({ applicationUrl, eoiButtons, slug, trackingProductId }) => {
  const theme = useDegreeDescriptionPageTheme();
  const eoiButton = eoiButtons.find((eoi) => eoi.actionType === ACTION_TYPES.emailMeInfo);

  let eoiText = applicationUrl ? _t('View application now') : _t('Request info');
  let description = applicationUrl
    ? _t('View the application now and take the first step towards earning your own degree')
    : _t('Request information now and take the first step towards earning your own degree');

  if (isPerformancePathwayDegree(slug)) {
    eoiText = _t('Enroll now');
    description = _t('View enrollment info now and take the first step towards earning your own degree');
  }

  return (
    <div css={styles.lastSlideContent}>
      <Typography2 variant="titleSmall" component="h2" color="invertBody" css={theme.mobileFontSizeStyle?.header}>
        {_t('Unlock your own success story')}
      </Typography2>
      {/* eslint-disable-next-line @coursera-web/custom-rules/prefer-cds-typography2 -- FIXME: legacy; migrate to Typography2 */}
      <Typography variant="h2" component="h3" color="invertBody" css={styles.textMargin}>
        {description}
      </Typography>
      {eoiButton && (
        <div css={styles.ctaButton}>
          <EoiButton
            actionConfig={eoiButton}
            expressionOfInterestText={{ emailMeButtonText: eoiText }}
            key={`${eoiButton?.actionType}-eoi-testimonial-button`}
            trackingProductId={trackingProductId}
          />
        </div>
      )}
      {!eoiButton && applicationUrl && (
        <Button
          component="a"
          href={applicationUrl}
          target="_blank"
          type="submit"
          variant="secondary"
          css={styles.ctaButton}
        >
          {eoiText}
        </Button>
      )}
    </div>
  );
};

type TestimonialCollection = DdpFieldsAboveFoldFragment['testimonialsCollection'];

type Props = {
  testimonials: TestimonialCollection;
  applicationUrl?: string | null;
  eoiButtons: EoiButtonsType;
  slug: string;
};

export const TestimonialsModule = ({ testimonials, applicationUrl, eoiButtons, slug }: Props) => {
  const { useGetViewPageSectionRef, trackingProductId } = useDegreeDescriptionEventingContext();

  const carouselSettings = {
    dots: true,
    dotsClass: 'carousel-slider-dots',
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    initialSlide: 0,
    adaptiveHeight: true,
    useTransform: false,
    prevArrow: (
      <div css={styles.carouselButtonIconWrap}>
        <IconButton intent="previous" variant="secondary" />
      </div>
    ),
    nextArrow: (
      <div css={styles.carouselButtonIconWrap}>
        <IconButton intent="next" variant="secondary" />
      </div>
    ),
    responsive: [
      {
        breakpoint: breakpoints.values.sm,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          initialSlide: 0,
        },
      },
    ],
  };

  const testimonialCTASlide = {
    image: {
      url: imgix.processImage(TestimonialCTAImage, {
        width: 800,
        height: 419,
      }),
      width: 800,
      height: 419,
      title: '',
      description: '',
    },
    lastSlideCTA: (
      <TestimonialCTA
        applicationUrl={applicationUrl}
        eoiButtons={eoiButtons}
        slug={slug}
        trackingProductId={trackingProductId}
      />
    ),
    isLastSlide: true,
    videoEmbedUrl: undefined,
    videoPlaceholderImage: undefined,
    authorName: undefined,
    authorTitle: undefined,
    text: undefined,
    authorOrganization: undefined,
  };

  const testimonialItems = useMemo(
    () => testimonials?.items.map((item) => ({ ...item, isLastSlide: false, lastSlideCTA: null })),
    [testimonials?.items]
  );

  const pageSectionRef = useGetViewPageSectionRef('learner_testimonials');

  if (!testimonialItems) {
    return null;
  }

  const showTestimonialCTA = testimonialItems.length > 0;
  const itemsToDisplay = showTestimonialCTA ? [...testimonialItems, testimonialCTASlide] : testimonialItems;

  // This handles an unaddressed bug in react-slick
  // https://github.com/akiran/react-slick/issues/2093
  if (testimonialItems.length < carouselSettings.slidesToShow) {
    carouselSettings.infinite = false;
  }

  return (
    <div css={[styles.sliderDots, styles.carouselButtons, styles.carouselWrap]} ref={pageSectionRef}>
      <Carousel settings={carouselSettings}>
        {itemsToDisplay?.map((testimonial, index) => {
          if (!testimonial) {
            return null;
          }

          const {
            image,
            videoEmbedUrl,
            videoPlaceholderImage,
            authorName,
            authorTitle,
            text,
            authorOrganization,
            isLastSlide,
            lastSlideCTA,
          } = testimonial;

          const shouldShowVideo = !!videoPlaceholderImage && !!videoEmbedUrl;
          const shouldShowImage = !shouldShowVideo;
          const imgAltText = authorName ? `${authorName}, ${authorOrganization ?? ''}` : '';

          return (
            <div key={`slide-${authorName}-${authorTitle}-${authorOrganization}`}>
              <div css={styles.testimonialCard}>
                <Grid container alignItems="center" justifyContent="center" css={styles.testimonialGrid}>
                  {shouldShowVideo && text && authorName && (
                    <Grid item xs={12} md={6}>
                      <TestimonialVideo
                        quoteText={text}
                        videoEmbedUrl={videoEmbedUrl}
                        placeholderImage={videoPlaceholderImage}
                        index={index}
                        authorName={authorName}
                        authorTitle={authorTitle}
                      />
                    </Grid>
                  )}

                  {shouldShowImage && image?.url && image?.width && image?.height && (
                    <Grid
                      item
                      xs={12}
                      md={6}
                      css={isLastSlide ? styles.lastSlideImageContainer : styles.imageContainer}
                    >
                      <Imgix
                        src={image.url}
                        maxWidth={image.width}
                        maxHeight={image.height}
                        {...(isLastSlide && { role: 'presentation' })}
                        alt={
                          isLastSlide ? '' : image?.description || _t('Testimonial by #{imgAltText}', { imgAltText })
                        }
                      />
                    </Grid>
                  )}

                  <Grid item xs={12} md={6}>
                    {!isLastSlide && text && authorName ? (
                      <Quote
                        quoteText={text}
                        authorName={authorName}
                        authorTitle={authorTitle}
                        authorOrg={authorOrganization}
                        index={index}
                      />
                    ) : (
                      lastSlideCTA
                    )}
                  </Grid>
                </Grid>
              </div>
            </div>
          );
        })}
      </Carousel>
    </div>
  );
};

export default React.memo(TestimonialsModule);
